import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../../shell/VenueContext";
import { monthMatrix, monthRange, shiftMonth, totalsByDay } from "../calendar";
import { useCalendarDensity } from "../queries";
import type { BookingFilter } from "../searchParams";

const WEEKDAY_KEYS = [
  "bookings.day.mon",
  "bookings.day.tue",
  "bookings.day.wed",
  "bookings.day.thu",
  "bookings.day.fri",
  "bookings.day.sat",
  "bookings.day.sun",
];

/**
 * Calendar view: month density in venue-local time (localization-i18n R5). Reads the calendar-density
 * endpoint (one bucket per day×status) and shows a per-day booking count; picking a day drills into
 * that day's timeline. Switching to this view never mutates — it re-presents the same dataset.
 */
export function CalendarView({
  anchor,
  filter,
  onPickDay,
  onShiftMonth,
}: {
  anchor: string;
  filter: BookingFilter;
  onPickDay: (day: string) => void;
  onShiftMonth: (nextAnchor: string) => void;
}) {
  const { venueId } = useVenue();
  const { t, i18n } = useTranslation();
  const { from, to } = monthRange(anchor);
  const density = useCalendarDensity(venueId, from, to, filter);
  const totals = totalsByDay(density.data ?? []);
  const weeks = monthMatrix(anchor);
  const monthLabel = new Intl.DateTimeFormat(i18n.language.startsWith("en") ? "en-US" : "fr-FR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${from}T00:00:00Z`));

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
      <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
        <button
          type="button"
          className={navBtn}
          aria-label={t("bookings.calendar.prevMonth")}
          onClick={() => onShiftMonth(shiftMonth(anchor, -1))}
        >
          ‹
        </button>
        <span className={css({ fontWeight: "semibold", minW: "40", textAlign: "center" })}>
          {monthLabel}
        </span>
        <button
          type="button"
          className={navBtn}
          aria-label={t("bookings.calendar.nextMonth")}
          onClick={() => onShiftMonth(shiftMonth(anchor, 1))}
        >
          ›
        </button>
      </div>

      <table
        aria-label={t("bookings.calendar.grid")}
        className={css({ width: "full", tableLayout: "fixed", borderCollapse: "collapse" })}
      >
        <thead>
          <tr>
            {WEEKDAY_KEYS.map((k) => (
              <th
                key={k}
                scope="col"
                className={css({ fontSize: "xs", color: "fg.muted", p: "1", fontWeight: "medium" })}
              >
                {t(k)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]?.date ?? ""}>
              {week.map((cell) => {
                const count = totals[cell.date] ?? 0;
                return (
                  <td key={cell.date} className={css({ p: "0.5", verticalAlign: "top" })}>
                    <button
                      type="button"
                      onClick={() => onPickDay(cell.date)}
                      className={css({
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "1",
                        width: "full",
                        minH: "16",
                        p: "1.5",
                        borderWidth: "1px",
                        borderColor: "border.default",
                        rounded: "sm",
                        bg: cell.inMonth ? "bg.default" : "bg.subtle",
                        opacity: cell.inMonth ? "1" : "0.6",
                        textAlign: "left",
                        _hover: { bg: "bg.muted" },
                        _focusVisible: {
                          outlineWidth: "2px",
                          outlineColor: "colorPalette.default",
                        },
                      })}
                    >
                      <span className={css({ fontSize: "xs", fontWeight: "medium" })}>
                        {Number(cell.date.slice(8, 10))}
                      </span>
                      {count > 0 ? (
                        <span
                          className={css({
                            fontSize: "xs",
                            px: "1.5",
                            rounded: "full",
                            bg: "booking.confirmed.bg",
                            color: "booking.confirmed.fg",
                          })}
                        >
                          {t("bookings.calendar.count", { count })}
                        </span>
                      ) : null}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const navBtn = css({
  px: "2",
  py: "1",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "md",
  lineHeight: "1",
});
